import { Box, Slider, Typography, FormHelperText } from "@mui/material";
import { FastField, getIn } from "formik";
import { useTranslation } from "react-i18next";
import { Slider as SteppedSlider } from "../../../components/Controls/Slider";
import { Colors } from "../../../utils/style/colors";
import { humanizeSettingKey } from "../utils";

// V3-parity: man-down motion sensitivity = 5 steps → 20/65/110/155/200.
const MOTION = { min: 20, max: 200, stepCount: 5, default: 110 };

export default function RangeInput({ id, name, schema = {}, onChange, disabled }) {
  const { t } = useTranslation();
  const isMotion = name === "motionSensitivity";

  // Reuse the V3 stepped Slider (endpoint labels ABOVE the track) + V3 wording.
  if (isMotion) {
    return (
      <FastField name={id}>
        {({ field, form }) => (
          <Box
            sx={{
              py: 0.75,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.95rem" }}>
                {t("movementSensitivity", { defaultValue: "Motion sensitivity" })}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: Colors.gray50 }}>
                {t("movementSensitivity.explanation.cancel", {
                  defaultValue:
                    "How much movement should there be to cancel the pre-alarm?",
                })}
              </Typography>
            </Box>
            <Box sx={{ pt: 2, pr: 0.5 }}>
              <SteppedSlider
                width={230}
                marks
                value={field.value}
                min={MOTION.min}
                max={MOTION.max}
                stepCount={MOTION.stepCount}
                disabled={disabled}
                minLabel={t("movement.none", {
                  defaultValue: "Almost no movement",
                })}
                maxLabel={t("movement.some", { defaultValue: "Some movement" })}
                onChange={(v) => form.setFieldValue(field.name, v)}
                onChangeCommitted={(_, v) => onChange(field.name, v)}
                onValueNotFound={() =>
                  form.setFieldValue(field.name, MOTION.default)
                }
              />
            </Box>
          </Box>
        )}
      </FastField>
    );
  }

  // Generic slider: bounds from the schema (minimum/maximum or validations).
  const { validations } = schema;
  const min = schema.minimum ?? validations?.min ?? 0;
  const max = schema.maximum ?? validations?.max ?? 100;
  const step = schema.step ?? validations?.step ?? 1;

  return (
    <FastField name={id}>
      {({ field, form }) => {
        const touched = getIn(form.touched, field.name);
        const error = getIn(form.errors, field.name);

        return (
          <Box
            sx={{
              py: 0.75,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography sx={{ flex: 1, fontSize: "0.95rem" }}>
              {t(name, { defaultValue: humanizeSettingKey(name) })}
            </Typography>

            <Box>
              <Slider
                value={typeof field.value === "number" ? field.value : min}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                sx={{ width: { xs: "150px", xl: "250px" } }}
                valueLabelDisplay="auto"
                marks={[
                  { label: min, value: min },
                  { label: max, value: max },
                ]}
                onChange={(_, value) => form.setFieldValue(field.name, value)}
                onChangeCommitted={(_, value) => onChange(field.name, value)}
              />

              {touched && error && (
                <FormHelperText error>{error}</FormHelperText>
              )}
            </Box>
          </Box>
        );
      }}
    </FastField>
  );
}
